"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* ─────────────────────────────────────────────
   1. Next-level license management
   ───────────────────────────────────────────── */
export function ResellerLicenseManagement({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Title */}
        <div className="mb-12">
          <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight leading-tight mb-8 text-white ${caveat.className}`}>
            <span className="x_wd_green_highlight_05 mr-3">Next-level</span>
            license management
          </h2>
        </div>

        {/* 3-Column Paragraph Layout */}
        <div className="row g-5 mb-16">
          <div className="col-lg-4">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">Streamlined purchasing.</strong> When buying a license, the purchase order is automatically created and sent to the vendor.
            </p>
          </div>
          <div className="col-lg-4">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">Detailed tracking.</strong> Odoo allows you to monitor license keys, user and developer headcounts, software versions, DB sizes, CPU numbers, and more.
            </p>
          </div>
          <div className="col-lg-4">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              Licenses are flagged when renewal is due, allowing for an easy follow-up.
            </p>
          </div>
        </div>

        {/* Overlapping Mockups Row */}
        <div className="row justify-content-center pt-8 relative z-10">
          <div className="col-lg-10 position-relative flex items-center justify-center min-h-[380px] lg:min-h-[520px] pb-5">
            {/* Left-aligned, right-rounded backdrop dome shape */}
            <div className="absolute top-0 bottom-[10%] left-[-50vw] right-[8%] bg-white/[0.03] rounded-r-[180px] md:rounded-r-[240px] -z-10 pointer-events-none select-none" />

            {/* Tilted Computer Icon */}
            <Image 
              src="/odoo/icons/green_computer.svg" 
              width={80}
              height={80}
              className="d-none d-lg-inline position-absolute top-[10%] start-[2%] z-30 select-none pointer-events-none h-20 w-auto rotate-[340deg]" 
              alt="" 
            />

            {/* Background Image: Sale Orders mockup */}
            <div className="absolute top-[28%] left-[34%] w-[58%] z-10">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.45)] overflow-hidden hover:scale-[1.01] transition-transform duration-300 bg-[#151517]"
              >
                <Image
                  src="/odoo/industries/software-reseller/sale-orders.webp"
                  width={700}
                  height={450}
                  className="w-full h-auto object-contain"
                  alt="Sales orders dashboard"
                />
              </motion.div>
            </div>

            {/* Foreground Image: License details mockup */}
            <div className="absolute top-[5%] left-[5%] w-[58%] z-20">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.55)] overflow-hidden hover:scale-[1.01] transition-transform duration-300 bg-[#151517]"
              >
                <Image
                  src="/odoo/industries/software-reseller/license.webp"
                  width={700}
                  height={450}
                  className="w-full h-auto object-contain"
                  alt="License management"
                />
              </motion.div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. Centralized project management
   ───────────────────────────────────────────── */
export function ResellerProjectManagement({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Heading & Subtext */}
        <div className="text-center max-w-[900px] mx-auto mb-16 space-y-4">
          <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight text-center ${caveat.className}`}>
            <span className="x_wd_blue_highlight_02 text-white">Centralized</span>{" "}
            project management
          </h2>
          <p className="text-gray-300 leading-relaxed max-w-2xl mx-auto text-xl">
            Odoo's project app allows you to view and follow up on all your ongoing implementations from one dashboard.
          </p>
        </div>

        {/* Screenshot mockup with layout callout annotations */}
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9 position-relative">
            
            {/* Annotations: Left Side (Desktop Only) */}
            <div className="hidden lg:block absolute left-[-220px] top-[10%] w-[190px] text-right z-30 pointer-events-none select-none">
              <div className="mb-14">
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Status filter</h4>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-xs text-gray-400">Filter tasks by stage</span>
                  <Image
                    src="/odoo/arrows/grey_arrow_sm_03.svg"
                    width={20}
                    height={20}
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    className="rotate-300 w-5 h-auto"
                    alt=""
                  />
                </div>
              </div>
              <div className="mb-14">
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Tags</h4>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-xs text-gray-400">Classify implementations</span>
                  <Image
                    src="/odoo/arrows/grey_arrow_sm_02.svg"
                    width={20}
                    height={20}
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    className="rotate-320 w-5 h-auto"
                    alt=""
                  />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Subtasks</h4>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-xs text-gray-400">Split project milestones</span>
                  <Image
                    src="/odoo/arrows/grey_arrow_sm_03.svg"
                    width={20}
                    height={20}
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    className="rotate-300 w-5 h-auto"
                    alt=""
                  />
                </div>
              </div>
            </div>

            {/* Annotations: Right Side (Desktop Only) */}
            <div className="hidden lg:block absolute right-[-220px] top-[10%] w-[190px] text-left z-30 pointer-events-none select-none">
              <div className="mb-14">
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Related records</h4>
                <div className="flex items-center gap-2">
                  <Image
                    src="/odoo/arrows/grey_arrow_sm_03.svg"
                    width={20}
                    height={20}
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    className="rotate-60 w-5 h-auto"
                    alt=""
                  />
                  <span className="text-xs text-gray-400">Track timesheets & sales</span>
                </div>
              </div>
              <div className="mb-14">
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Multiple Views</h4>
                <div className="flex items-center gap-2">
                  <Image
                    src="/odoo/arrows/grey_arrow_sm_02.svg"
                    width={20}
                    height={20}
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    className="rotate-140 w-5 h-auto"
                    alt=""
                  />
                  <span className="text-xs text-gray-400">Kanban, Gantt & List</span>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Task Statuses</h4>
                <div className="flex items-center gap-2">
                  <Image
                    src="/odoo/arrows/grey_arrow_sm_03.svg"
                    width={20}
                    height={20}
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    className="rotate-130 w-5 h-auto"
                    alt=""
                  />
                  <span className="text-xs text-gray-400">Block or request help</span>
                </div>
              </div>
            </div>

            {/* Main Projects Mockup image */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.6)] overflow-hidden hover:scale-[1.005] transition-transform duration-300 bg-[#151517]"
            >
              <Image
                src="/odoo/industries/software-reseller/projects.webp"
                width={900}
                height={600}
                className="w-full h-auto object-contain"
                alt="Odoo project management dashboard"
              />
            </motion.div>

            {/* Mobile Callout Checklist (visible on mobile only) */}
            <div className="block lg:hidden mt-8 grid grid-cols-2 gap-4 bg-white/[0.02] border border-white/5 rounded-2xl p-6 text-sm">
              <div>
                <span className="font-bold text-sky-400 block mb-1">✓ Status Filter</span>
                <span className="text-gray-400">Filter tasks by stages.</span>
              </div>
              <div>
                <span className="font-bold text-sky-400 block mb-1">✓ Related Records</span>
                <span className="text-gray-400">Track timesheets & sales.</span>
              </div>
              <div>
                <span className="font-bold text-sky-400 block mb-1">✓ Tags</span>
                <span className="text-gray-400">Classify implementations.</span>
              </div>
              <div>
                <span className="font-bold text-sky-400 block mb-1">✓ Multiple Views</span>
                <span className="text-gray-400">Kanban, Gantt & List.</span>
              </div>
              <div>
                <span className="font-bold text-sky-400 block mb-1">✓ Subtasks</span>
                <span className="text-gray-400">Split project milestones.</span>
              </div>
              <div>
                <span className="font-bold text-sky-400 block mb-1">✓ Task Statuses</span>
                <span className="text-gray-400">Block or request help.</span>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. Admin work made easy
   ───────────────────────────────────────────── */
export function ResellerAdminWork({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Content Row */}
        <div className="row align-items-center justify-content-between g-5">
          
          {/* Left Column: Text descriptions */}
          <div className="col-lg-6 col-xl-5 pb-5 space-y-6">
            <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight ${caveat.className}`}>
              Admin work <br/>
              made <span className="x_wd_yellow_highlight_bold_04">easy</span>
            </h2>
            <div className="space-y-6">
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Flexible invoicing.</strong> Create recurring invoices for licenses, and use timesheets, milestones, or fixed rates to accurately bill your customers for services.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Beautiful quotations.</strong> Build a stunning quote for each sale in seconds, and reuse templates for common license and implementation packages.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Intuitive planning.</strong> It's easy to get lost when handling multiple projects. Planning app allows you to oversee your team's planning in an easy-to-use Gantt chart.
              </p>
            </div>
          </div>

          {/* Right Column: Invoicing mockup card with backdrop shape */}
          <div className="col-lg-5 col-xl-6 offset-xl-1 py-5 position-relative flex justify-center items-center">
            {/* Right-aligned, rounded-left dome shape */}
            <div className="absolute top-0 bottom-0 left-[15%] right-[-100vw] bg-white/[0.03] rounded-l-[180px] md:rounded-l-[240px] -z-10 pointer-events-none select-none" />

            {/* Tilted Money Plant Icon */}
            <Image 
              src="/odoo/icons/yellow_money-plant.svg" 
              width={80}
              height={80}
              className="z-30 d-none d-lg-inline position-absolute top-[-10px] start-[90%] translate-middle rotate-10 h-20 w-auto" 
              style={{ filter: "hue-rotate(-20deg) saturate(1.5)", height: "80px", width: "auto" }}
              alt="" 
            />

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.55)] overflow-hidden hover:scale-[1.01] transition-transform duration-300 bg-[#151517] w-full"
            >
              <Image
                src="/odoo/industries/software-reseller/invoice.webp"
                width={800}
                height={550}
                className="w-full h-auto object-contain"
                alt="Invoicing dashboard"
              />
            </motion.div>
          </div>

        </div>

        {/* Secondary Testimonial (Juan Tovar) */}
        <div className="row g-0 justify-content-between bg-white/[0.03] border border-white/10 rounded-3xl p-6 lg:p-10 position-relative mt-24 max-w-[1100px] mx-auto align-items-center">
          <div className="absolute inset-0 bg-orange-500/5 rounded-3xl blur-[120px] pointer-events-none -z-10" />
          
          <Image
            src="/odoo/snippets/s_wd_testimonials/quote.svg"
            width={70}
            height={70}
            className="position-absolute top-0 translate-middle d-none d-md-inline opacity-30 brightness-0 invert"
            style={{ width: "70px", height: "auto" }}
            alt=""
          />
          
          <div className="col-lg-8 pb-4 pb-lg-0">
            <blockquote className="blockquote border-0 p-0 text-lg md:text-xl text-gray-200 font-normal leading-relaxed italic">
              "The workflows the Odoo has implemented and the way they present them are key. It's easy and visually appealing to whoever is using it."
            </blockquote>
          </div>
          
          <div className="col-lg-4 d-sm-flex flex-lg-column align-items-center align-items-lg-end text-sm">
            <Image
              height={90}
              width={90}
              className="d-none d-sm-block rounded-full mt-lg-n10 mb-lg-4 me-4 me-lg-0 border-2 border-white/20 shadow-lg object-cover"
              src="/odoo/snippets/s_wd_testimonials/avatar/juan_tovar.webp"
              alt="Juan Tovar picture"
            />
            <div className="flex-grow-1 text-left text-xl-end">
              <b className="text-white text-base">Juan Tovar</b>
              <span className="d-block text-gray-400">Senior VP of Corporate Integrations</span>
            </div>
          </div>
        </div>

        {/* Callout (Bubu Persona) */}
        <div className="w-full flex justify-center mt-20 select-none pointer-events-none">
          <div className="d-flex align-items-center justify-content-center gap-4">
            <Image
              src="/odoo/arrows/grey_arrow_loop_02.svg"
              width={100}
              height={100}
              style={{ transform: "scaleX(-1)", height: "100px", width: "auto" }}
              className="brightness-0 invert opacity-45 rotate-50 flip h-[100px] w-auto mt-4 d-none d-sm-block"
              alt=""
            />
            <Image
              width={60}
              height={60}
              className="d-none d-md-inline-block rounded-full border border-white/20 shadow-lg"
              alt=""
              src="/odoo/snippets/s_wd_persona/bubu.webp"
            />
            <div className="bg-white/10 border border-white/20 rounded-full shadow-2xl p-4 px-8 backdrop-blur-md">
              <i className="text-white text-base font-semibold ">But wait! There's more!</i>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
